import { GRID_SIZE, SPAN } from './constants';
import { LATEST_SOLVER_EPOCH } from './epochs';
import { cheb, clonePoint, key } from './geometry';
import { mulberry32, type Rng } from './rng';
import { mstFloor, mwFor, statsFor } from './scoring';
import type { Point, Puzzle, ScoredPuzzle, Solution, SolverEpoch, Stats } from './types';

/**
 * One reference search, frozen under its epoch number (GDD §3.5).
 *
 * A grid's 🎯 is produced on the device, so the search *is* part of the
 * puzzle: change it and you have changed a race two players already compared.
 * An epoch therefore owns both halves of what makes its answer — the constants
 * it is tuned at and the entry point that reads them — and a better search is
 * a *second* record beside the first, registered under a new key in
 * {@link SEARCHES}.
 *
 * Editing an epoch that has shipped is the one move that is never allowed.
 * `tests/unit/epochs.test.ts` pins the wire and MW of the early grids to
 * literal numbers so that rule fails loudly instead of quietly.
 */
interface SolverVersion {
  /** The constants this search is tuned at, frozen alongside it. */
  readonly tuning: Readonly<Record<string, number>>;
  /** Entry point. Deterministic in `(puzzle, seed)` and nothing else. */
  readonly search: (puzzle: Puzzle, seed: number) => Solution;
}

/*
 * Epoch 1 begins here: the multi-start hill climb the game shipped with, and
 * the search behind every target dealt so far. Its constants, its candidate
 * ordering, its tie-breaks and the order it sums floats in are all part of the
 * answer it gives, so nothing between here and `EPOCH_1` may be touched, and
 * nothing here is shared with a later epoch — epoch 2 writes its own.
 */

/** Epoch 1's tuning. Frozen: see {@link SolverVersion}. */
const EPOCH_1_TUNING = {
  /** Hill-climb iterations before we call a layout settled. */
  climbGuard: 60,
  /** Corridor-walk starts: feasible-by-construction layouts, walked plant→house. */
  walkStarts: 40,
  /** Kick restarts perturbing the running best. */
  kickStarts: 60,
  /** Two-tower polish sweeps before the local optimum is accepted. */
  polishRounds: 8,
  /** Mixed into the caller's seed so the search draws a stream of its own. */
  seedMix: 0x51a75eed,
} as const;

/**
 * Every cell a tower may legally stand on.
 *
 * Row-major, and the climb takes the first cell that ties, so this order is
 * part of epoch 1's answer rather than an implementation detail.
 */
function buildable(puzzle: Puzzle): Point[] {
  const blocked = new Set([
    key(puzzle.plant),
    ...puzzle.houses.map(key),
    ...Object.keys(puzzle.obstacles),
  ]);
  const cand: Point[] = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      const p = { r, c };
      if (!blocked.has(key(p))) cand.push(p);
    }
  }
  return cand;
}

/**
 * Steepest descent: each step tries removing a tower or relocating it to any
 * free cell, and takes the single change that cuts the most wire while
 * keeping every house lit. Returns null when the start doesn't light them.
 */
function climb(puzzle: Puzzle, cand: Point[], start: Point[]): Solution | null {
  let cur = start.map(clonePoint);
  let s = statsFor(puzzle, cur);
  if (!s.ok) return null;

  for (let guard = 0; guard < EPOCH_1_TUNING.climbGuard; guard++) {
    let move: { towers: Point[]; s: Stats } | null = null;
    let bw = s.wire - 1e-9;

    for (let i = 0; i < cur.length; i++) {
      const rest = cur.filter((_, j) => j !== i);
      const rs = statsFor(puzzle, rest);
      if (rs.ok && rs.wire < bw) {
        bw = rs.wire;
        move = { towers: rest, s: rs };
      }
      for (const c2 of cand) {
        if (cur.some((t) => key(t) === key(c2))) continue;
        const ts = statsFor(puzzle, rest.concat([c2]));
        if (ts.ok && ts.wire < bw) {
          bw = ts.wire;
          move = { towers: rest.concat([c2]), s: ts };
        }
      }
    }

    if (!move) break;
    cur = move.towers;
    s = move.s;
  }
  return { towers: cur, wire: s.wire };
}

/**
 * A feasible start the climb can't invent for itself: visit the houses in a
 * random order and walk towers from the nearest powered node toward each,
 * every step inside span and strictly closer. Random subsets almost never
 * light three houses, so seeding from these walks is what actually spreads
 * the restarts across the real search space.
 */
function corridorWalk(puzzle: Puzzle, cand: Point[], rnd: Rng): Point[] | null {
  const towers: Point[] = [];
  const nodes = [puzzle.plant];
  const used = new Set<string>();

  const houses = [...puzzle.houses];
  for (let i = houses.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [houses[i], houses[j]] = [houses[j], houses[i]];
  }

  for (const h of houses) {
    for (let guard = 0; guard < 12; guard++) {
      let near = nodes[0];
      for (const n of nodes) if (cheb(n, h) < cheb(near, h)) near = n;
      if (cheb(near, h) <= SPAN) break;
      const steps = cand.filter(
        (c) => !used.has(key(c)) && cheb(c, near) <= SPAN && cheb(c, h) < cheb(near, h),
      );
      if (!steps.length || towers.length >= puzzle.par) return null;
      const c = steps[Math.floor(rnd() * steps.length)];
      towers.push(c);
      nodes.push(c);
      used.add(key(c));
    }
    let near = nodes[0];
    for (const n of nodes) if (cheb(n, h) < cheb(near, h)) near = n;
    if (cheb(near, h) > SPAN) return null;
  }
  return towers;
}

/**
 * Exhaustive two-tower relocations, re-descending after each gain. The climb
 * is 1-opt optimal by construction; this is the systematic 2-opt pass that
 * closes the basins random kicks only sometimes stumble into.
 */
function polish(puzzle: Puzzle, cand: Point[], sol: Solution): Solution {
  let best = sol;
  for (let round = 0; round < EPOCH_1_TUNING.polishRounds; round++) {
    let better: Point[] | null = null;
    const cur = best.towers;
    for (let i = 0; i < cur.length && !better; i++) {
      for (let j = i + 1; j < cur.length && !better; j++) {
        const rest = cur.filter((_, m) => m !== i && m !== j);
        for (let a = 0; a < cand.length && !better; a++) {
          if (rest.some((t) => key(t) === key(cand[a]))) continue;
          const one = statsFor(puzzle, rest.concat([cand[a]]));
          if (one.ok && one.wire < best.wire - 1e-9) better = rest.concat([cand[a]]);
          for (let b = a + 1; b < cand.length && !better; b++) {
            if (rest.some((t) => key(t) === key(cand[b]))) continue;
            const two = statsFor(puzzle, rest.concat([cand[a], cand[b]]));
            if (two.ok && two.wire < best.wire - 1e-9) better = rest.concat([cand[a], cand[b]]);
          }
        }
      }
    }
    if (!better) break;
    best = climb(puzzle, cand, better) ?? { towers: better, wire: statsFor(puzzle, better).wire };
  }
  return best;
}

/**
 * Epoch 1's search: steepest descent from the generator's seed line and from
 * seeded corridor walks, kick restarts from the running best, then a two-tower
 * polish.
 *
 * Tuned (GDD §3.5) so a search ten times this size finds a shorter grid on
 * only ~2% of days, and then by under 2% of wire — beating the engineers
 * stays possible, rare, and thin.
 */
function bestKnownEpoch1(puzzle: Puzzle, seed: number): Solution {
  const rnd = mulberry32(seed ^ EPOCH_1_TUNING.seedMix);
  const cand = buildable(puzzle);

  const fromSeed = climb(puzzle, cand, puzzle.seedTowers);
  // The generator only emits puzzles whose seed layout lights every house, so
  // this holds; treat the seed line as the reference if a caller hands us
  // something looser.
  if (!fromSeed) {
    return {
      towers: puzzle.seedTowers.map(clonePoint),
      wire: statsFor(puzzle, puzzle.seedTowers).wire,
    };
  }

  let best: Solution = fromSeed;
  const consider = (r: Solution | null): void => {
    if (r && r.wire < best.wire - 1e-9) best = r;
  };

  for (let k = 0; k < EPOCH_1_TUNING.walkStarts; k++) {
    const walk = corridorWalk(puzzle, cand, rnd);
    if (walk) consider(climb(puzzle, cand, walk));
  }

  for (let k = 0; k < EPOCH_1_TUNING.kickStarts; k++) {
    const base = best.towers.map(clonePoint);
    const kicks = 1 + Math.floor(rnd() * 3);
    for (let m = 0; m < kicks && base.length; m++) {
      base[Math.floor(rnd() * base.length)] = cand[Math.floor(rnd() * cand.length)];
    }
    const uniq = [...new Map(base.map((t) => [key(t), t])).values()].slice(0, puzzle.par);
    consider(climb(puzzle, cand, uniq));
  }

  return polish(puzzle, cand, best);
}

/** Epoch 1, sealed. */
const EPOCH_1: SolverVersion = { tuning: EPOCH_1_TUNING, search: bestKnownEpoch1 };

/* Epoch 1 ends here. A new epoch goes below, and leaves the above alone. */

/**
 * Every reference search the game has shipped, keyed by epoch.
 *
 * Improving the search adds a key; it never rewrites one. The epoch a grid
 * number resolves to lives in `epochs.ts`, so a new search reaches only grid
 * numbers dealt after its release.
 */
const SEARCHES: Record<SolverEpoch, SolverVersion> = { 1: EPOCH_1 };

/**
 * Best-known grid: the deterministic search every player races (GDD §3.5).
 *
 * `epoch` decides which frozen search answers. Callers holding a grid number
 * should pass `solverEpochFor(day)`; the default is the newest search, which
 * is right where there is no grid number, as in practice and drills.
 */
export function bestKnown(
  puzzle: Puzzle,
  seed: number,
  epoch: SolverEpoch = LATEST_SOLVER_EPOCH,
): Solution {
  return SEARCHES[epoch].search(puzzle, seed);
}

/**
 * Resolve a puzzle's scoring reference points, stamped with the search that
 * produced them. Expensive — do it once per grid.
 */
export function score(
  puzzle: Puzzle,
  seed: number,
  epoch: SolverEpoch = LATEST_SOLVER_EPOCH,
): ScoredPuzzle {
  const withFloor = { ...puzzle, mst: mstFloor(puzzle), solverEpoch: epoch } as ScoredPuzzle;
  withFloor.ref = bestKnown(puzzle, seed, epoch);
  withFloor.refMW = mwFor(withFloor, withFloor.ref.wire);
  return withFloor;
}
